import {useState, useCallback, useRef, useMemo, type ChangeEvent, type DragEvent} from 'react'
import {Typography, Box, Button} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, useAppSelector} from '../../../common/store'
import {setScreenImageUrl, selectActiveScene} from '../../../common/components/store/scenesSlice'
import {trackEvent} from '../../../common/analytics/trackEvent'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'

// ── Helpers ───────────────────────────────────────────────────────

const loadImageFile = (file: File): Promise<string> =>
  new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result)
    }
    reader.readAsDataURL(file)
  })

// ── FreeScreenImagePanel — flat screen image upload (no accordion) ─

const FreeScreenImagePanel = () => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const dispatch = useAppDispatch()

  const screenImageUrl = useAppSelector((s) => selectActiveScene(s).screenImageUrl)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      const dataUrl = await loadImageFile(file)
      dispatch(setScreenImageUrl(dataUrl))
      trackEvent('asset_uploaded', {kind: 'screen', format: file.type, size_bytes: file.size, editor_mode: 'free'})
      e.target.value = ''
    },
    [dispatch],
  )

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setDragOver(false)
  }, [])

  const handleDrop = useCallback(
    async (e: DragEvent) => {
      e.preventDefault()
      setDragOver(false)
      const file = e.dataTransfer.files[0]
      if (!file) return
      if (!file.type.startsWith('image/')) {
        trackEvent('asset_upload_rejected', {kind: 'screen', reason: 'bad_format', editor_mode: 'free'})
        return
      }
      const dataUrl = await loadImageFile(file)
      dispatch(setScreenImageUrl(dataUrl))
      trackEvent('asset_uploaded', {kind: 'screen', format: file.type, size_bytes: file.size, editor_mode: 'free'})
    },
    [dispatch],
  )

  const sx = useMemo(() => buildStyles(tokens), [tokens])

  return (
    <Box sx={sx.root}>
      <Box
        sx={{...sx.dropZone, ...(dragOver ? sx.dropZoneActive : {})}}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Box component="img" src={screenImageUrl} alt={t('a11y.screenPreview')} sx={sx.imagePreview} />
        {dragOver && (
          <Box sx={sx.dropOverlay}>
            <Typography sx={sx.dropText}>{t('screenImage.dropHere')}</Typography>
          </Box>
        )}
      </Box>

      <Button
        variant="outlined"
        size="small"
        startIcon={<UploadFileIcon />}
        onClick={handleUploadClick}
        sx={sx.uploadButton}
      >
        {t('screenImage.upload')}
      </Button>
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} style={{display: 'none'}} />
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  root: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
    alignItems: 'stretch',
  },
  dropZone: {
    position: 'relative' as const,
    borderRadius: '6px',
    border: `2px dashed ${tokens.accordion.border}`,
    overflow: 'hidden',
    transition: 'border-color 0.2s',
    cursor: 'pointer',
  },
  dropZoneActive: {
    borderColor: tokens.text.primary,
  },
  imagePreview: {
    width: '100%',
    height: 120,
    objectFit: 'contain',
    display: 'block',
  },
  dropOverlay: {
    position: 'absolute' as const,
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(2px)',
  },
  dropText: {
    fontSize: 12,
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  uploadButton: {
    fontSize: 11,
    textTransform: 'none' as const,
    color: tokens.text.secondary,
    borderColor: tokens.accordion.border,
    '&:hover': {
      borderColor: tokens.text.muted,
    },
  },
})

export default FreeScreenImagePanel
